import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { CircularProgress, DialogContent } from "@material-ui/core";
import Typography from "@mui/material/Typography";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { ValidatorForm } from 'react-material-ui-form-validator';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Typography2 from '@mui/material/Typography';
import { PRIMARY_THEME_COLOR_SHADE_1 } from "../../../lib/constants";
import ManageRoles from '../../UsersNew/Component/ManageRoles';
import AlertDialog from '../../../components/AlertDialog/index.es6';
import ButtonInc from '../../../shared-ui/ButtonInc';
import { getPartnerGuid } from '../../../lib/access.es6';
import {
  buildUserUpdatePayload,
  withActivePartner,
} from '../../UsersNew/Component/userUpdatePayload';

const RESERVED_ATTRS = ['partners', 'isAgent'];

function parseAttrs(userAttrs) {
  return Object.keys(userAttrs)
    .filter(function(key) { return RESERVED_ATTRS.indexOf(key) === -1; })
    .map(function(key) {
      return { key: key, value: userAttrs[key][0] || '', isNew: false };
    });
}

function formatAttrKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^[a-z]/, function(c) { return c.toUpperCase(); });
}

const EditPartnerUser = (props) => {
  if (!props.user) {
    return '';
  }

  var userAttrs = props.user.attributes || {};
  var partnerGuid = getPartnerGuid();

  const [isAgent, setIsAgent] = useState(userAttrs.isAgent ? userAttrs.isAgent[0] === 'true' : false);
  const [dynamicAttrs, setDynamicAttrs] = useState(parseAttrs(userAttrs));

  const addAttr = () => {
    setDynamicAttrs(function(prev) {
      return prev.concat([{ key: '', value: '', isNew: true }]);
    });
  };

  const removeAttr = (index) => {
    setDynamicAttrs(function(prev) {
      return prev.filter(function(_, i) { return i !== index; });
    });
  };

  const handleAttrKeyChange = (index, key) => {
    setDynamicAttrs(function(prev) {
      return prev.map(function(a, i) {
        return i === index ? Object.assign({}, a, { key: key }) : a;
      });
    });
  };

  const handleAttrValueChange = (index, value) => {
    setDynamicAttrs(function(prev) {
      return prev.map(function(a, i) {
        return i === index ? Object.assign({}, a, { value: value }) : a;
      });
    });
  };

  const handleProfileSubmit = () => {
    var attrsPayload = {};
    dynamicAttrs.forEach(function(a) {
      if (a.key) {
        attrsPayload[a.key] = [a.value];
      }
    });
    attrsPayload.partners = withActivePartner(userAttrs.partners, partnerGuid);
    attrsPayload.isAgent = [String(isAgent)];

    props.editUser(buildUserUpdatePayload(props.user, {
      firstName: props.user.firstName,
      lastName: props.user.lastName,
      attributes: attrsPayload,
    }));
  };

  return (
    <>
      <AlertDialog
        custom
        show={props.showEditUserProgressAlert}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        confirmBtnText={'ok'}
        showCancel={false}
        showConfirm={false}
        cancelBtnText={'cancel'}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Updating User</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.showEditUserSuccessAlert}
        size="sm"
        title={'User updated'}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={props.resetEditUserSuccessAlert}
        confirmBtnText={'ok'}
        showCancel={false}
        showConfirm={false}
        cancelBtnText={'cancel'}
        confirmBtnCssClass
      >
        <div>
          <Button variant="contained" onClick={props.resetEditUserSuccessAlert} style={{ background: 'green' }}>Ok</Button>
        </div>
      </AlertDialog>

      <AlertDialog
        show={props.showEditUserErrorAlert}
        danger
        title={'Error updating user'}
        onConfirm={props.resetEditUserErrorAlert}
        confirmBtnText={'Try again'}
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >
        <p>{props.editUserError ? props.editUserError : 'Error updating user'}</p>
      </AlertDialog>

      <Dialog
        open={Boolean(props.user)}
        fullWidth
        maxWidth="lg"
        onClose={props.resetUserToEditRoles}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar style={{ background: PRIMARY_THEME_COLOR_SHADE_1 }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={props.resetUserToEditRoles}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              {'Edit User — ' + (props.user.firstName || '') + ' ' + (props.user.lastName || '')}
            </Typography>
          </Toolbar>
        </AppBar>

        <DialogContent>
          <Typography2 variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
            Profile
          </Typography2>

          <Grid container spacing={2} style={{ marginBottom: '8px' }}>
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  checked={isAgent}
                  onChange={function(e) { setIsAgent(e.target.checked); }}
                  control={<Checkbox size="small" />}
                  label="Is Agent?"
                  labelPlacement="start"
                />
              </FormGroup>
            </Grid>
          </Grid>

          <ValidatorForm onSubmit={handleProfileSubmit}>
            <Grid container spacing={2}>

              {dynamicAttrs.map(function(attr, index) {
                return (
                  <Grid item xs={12} key={index}>
                    <Grid container spacing={2} alignItems="center">
                      {attr.isNew && (
                        <Grid item md={5} sm={5} xs={12}>
                          <TextField
                            label="Attribute Name"
                            value={attr.key}
                            fullWidth
                            variant="outlined"
                            placeholder="e.g. staffId"
                            onChange={function(e) { handleAttrKeyChange(index, e.target.value); }}
                          />
                        </Grid>
                      )}
                      <Grid item md={attr.isNew ? 6 : 11} sm={attr.isNew ? 6 : 11} xs={10}>
                        <TextField
                          label={attr.isNew ? 'Value' : formatAttrKey(attr.key)}
                          value={attr.value}
                          fullWidth
                          variant="outlined"
                          onChange={function(e) { handleAttrValueChange(index, e.target.value); }}
                        />
                      </Grid>
                      <Grid item md={1} sm={1} xs={2} style={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                          onClick={function() { removeAttr(index); }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addAttr}
                  variant="outlined"
                  size="small"
                  style={{ borderColor: PRIMARY_THEME_COLOR_SHADE_1, color: PRIMARY_THEME_COLOR_SHADE_1 }}
                >
                  Add Attribute
                </Button>
              </Grid>

              <Grid item md={4} sm={6} xs={12} style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  type="submit"
                  fullWidth
                  style={{ background: PRIMARY_THEME_COLOR_SHADE_1, marginTop: '8px' }}
                >
                  Save Profile
                </Button>
              </Grid>

            </Grid>
          </ValidatorForm>

          <Divider style={{ margin: '24px 0' }} />

          <Typography2 variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
            Roles
          </Typography2>

          <Grid container spacing={4} style={{ marginTop: '2%' }}>
            <Grid item xs={12}>
              <ManageRoles
                user={props.user}
                partner={partnerGuid}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <div style={{ textAlign: 'center', marginBottom: '2%' }}>
          <ButtonInc onClick={props.resetUserToEditRoles} color="primary">
            Close
          </ButtonInc>
        </div>
      </Dialog>
    </>
  );
};

export default EditPartnerUser;
